/**
 * RFC 9457 problem details. Every error the API emits has a stable `type` URI, a
 * learner-safe `title`, and an explicit `retryable` flag so clients never have to guess
 * whether to retry (docs/07-tech-architecture.md §5).
 */

import { z } from 'zod';

export const PROBLEM_BASE = 'https://saysooner.com/problems';

export const PROBLEM_TYPES = {
  // 400-level
  validation: 'validation',
  unauthenticated: 'unauthenticated',
  forbidden: 'forbidden',
  notFound: 'not-found',
  conflict: 'conflict',
  staleVersion: 'stale-version',
  rateLimited: 'rate-limited',
  quotaExhausted: 'quota-exhausted',
  entitlementRequired: 'entitlement-required',
  contentModerated: 'content-moderated',
  receiptInvalid: 'receipt-invalid',
  // 500-level
  internal: 'internal',
  providerUnavailable: 'provider-unavailable',
  scoringUnavailable: 'scoring-unavailable',
  degraded: 'degraded',
} as const;

export type ProblemTypeKey = keyof typeof PROBLEM_TYPES;

export const ProblemSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number().int().min(400).max(599),
  detail: z.string().optional(),
  instance: z.string().optional(),
  /** Whether the client should retry the identical request. */
  retryable: z.boolean(),
  /** Seconds to wait before retrying, when known. */
  retryAfter: z.number().int().positive().optional(),
  /** Field-level validation issues. */
  issues: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
  /** Correlates a learner report with server logs end to end. */
  traceId: z.string().optional(),
});
export type Problem = z.infer<typeof ProblemSchema>;

interface ProblemSpec {
  readonly status: number;
  readonly title: string;
  readonly retryable: boolean;
}

const SPECS: Readonly<Record<ProblemTypeKey, ProblemSpec>> = {
  validation: { status: 400, title: 'That request was not valid.', retryable: false },
  unauthenticated: { status: 401, title: 'Please sign in again.', retryable: false },
  forbidden: { status: 403, title: "You don't have access to that.", retryable: false },
  notFound: { status: 404, title: "We couldn't find that.", retryable: false },
  conflict: { status: 409, title: 'That has already been done.', retryable: false },
  staleVersion: {
    status: 412,
    title: 'This was changed on another device. Refreshing now.',
    retryable: true,
  },
  rateLimited: { status: 429, title: 'Too many requests — give it a moment.', retryable: true },
  quotaExhausted: {
    status: 429,
    // Never framed as a failure; the limit sheet offers free alternatives.
    title: "You've used today's voice minutes.",
    retryable: false,
  },
  entitlementRequired: { status: 402, title: 'That feature is part of Plus.', retryable: false },
  contentModerated: {
    status: 422,
    title: "We can't build a scenario from that.",
    retryable: false,
  },
  receiptInvalid: { status: 422, title: "We couldn't verify that purchase.", retryable: true },
  internal: { status: 500, title: 'Something went wrong on our side.', retryable: true },
  providerUnavailable: {
    status: 503,
    title: 'Our tutor service is briefly unavailable.',
    retryable: true,
  },
  scoringUnavailable: {
    status: 503,
    // Scoring failure must never block a lesson; this is informational.
    title: "We couldn't score that just now — your lesson will continue.",
    retryable: true,
  },
  degraded: { status: 503, title: 'Running in text-only mode for now.', retryable: true },
};

export interface ProblemInit {
  readonly detail?: string;
  readonly instance?: string;
  readonly retryAfter?: number;
  readonly issues?: readonly { path: string; message: string }[];
  readonly traceId?: string;
}

export function problem(key: ProblemTypeKey, init: ProblemInit = {}): Problem {
  const spec = SPECS[key];
  return {
    type: `${PROBLEM_BASE}/${PROBLEM_TYPES[key]}`,
    title: spec.title,
    status: spec.status,
    retryable: spec.retryable,
    ...(init.detail !== undefined ? { detail: init.detail } : {}),
    ...(init.instance !== undefined ? { instance: init.instance } : {}),
    ...(init.retryAfter !== undefined ? { retryAfter: init.retryAfter } : {}),
    ...(init.issues !== undefined ? { issues: [...init.issues] } : {}),
    ...(init.traceId !== undefined ? { traceId: init.traceId } : {}),
  };
}

export function problemStatus(key: ProblemTypeKey): number {
  return SPECS[key].status;
}

/** Converts a Zod failure into a problem document without leaking internals. */
export function problemFromZod(error: {
  issues: readonly { path: readonly (string | number)[]; message: string }[];
}): Problem {
  return problem('validation', {
    issues: error.issues.map((i) => ({ path: i.path.join('.'), message: i.message })),
  });
}
